// Ordre du sentier « Apprendre », tel qu'affiché par CategorySelection :
// pour chaque unité → vocabulaire (parties d'au plus 20 mots) avec les
// conversations et la grammaire intercalées, puis phrases (parties).
// Les quiz culturels ne font pas partie de l'enchaînement « Suivant ».
//
// Sert à tous les boutons « Suivant » d'App.tsx pour que la navigation suive
// exactement le sentier. Garder ce découpage aligné sur CategorySelection.
import {
  UNIT_CONFIGS,
  CONVERSATION_TRAIL_CONFIGS,
  UNIT_SENTENCE_CATEGORIES,
  grammarSubMatchesUnit,
} from '../components/CategorySelection';
import { LESSON_BREAKS, TRAIL_SPLIT_SIZE, grammarSlots, vocabSplit } from './trailSplit';

export { TRAIL_SPLIT_SIZE };

export type TrailNavStep =
  | { kind: 'vocab'; unitId: number; category: string; range?: { start: number; end: number } }
  | { kind: 'conversation'; unitId: number; conversationId: string }
  | { kind: 'grammar'; unitId: number; subcats: string[] }
  | { kind: 'phrases'; unitId: number; cats: string[] };

/** Étapes réservées au Premium. */
export const isPremiumStep = (s: TrailNavStep): boolean =>
  s.kind === 'grammar' || s.kind === 'phrases';

// Mêmes règles que le sentier (CategorySelection.getVocabWordKeys hors cas Kama)
export const vocabWordKeys = (catData: Record<string, any> | undefined): string[] =>
  Object.keys(catData ?? {})
    .filter(k => !k.includes('_'))
    .filter(k => !catData?.[k]?.isSectionHeader);

/** Regroupe des éléments en parties d'environ TRAIL_SPLIT_SIZE (sans couper un élément). */
const groupParts = (items: Array<[string, number]>): string[][] => {
  const parts: string[][] = [];
  let cur: string[] = [];
  let total = 0;
  for (const [name, count] of items) {
    if (total + count > TRAIL_SPLIT_SIZE && cur.length > 0) {
      parts.push(cur);
      cur = [name];
      total = count;
    } else {
      cur.push(name);
      total += count;
    }
  }
  if (cur.length > 0) parts.push(cur);
  return parts;
};

export const grammarPartsForUnit = (grammarObj: Record<string, any> | undefined, unitNum: number): string[][] => {
  if (!grammarObj || typeof grammarObj !== 'object') return [];
  return groupParts(
    Object.keys(grammarObj)
      .filter(k => grammarSubMatchesUnit(k, unitNum))
      .map(k => [k, Object.keys(grammarObj[k] ?? {}).filter(w => !w.includes('_')).length] as [string, number])
  );
};

export const buildTrailSequence = (
  languageCode: string,
  categories: Record<string, any>,
  sentences: Array<{ category?: string; original?: string }> = []
): TrailNavStep[] => {
  const steps: TrailNavStep[] = [];
  const categoryList = Object.keys(categories);
  const presentInUnit = (unitId: number) =>
    (UNIT_CONFIGS.find(u => u.id === unitId)?.categories ?? []).filter(c => categoryList.includes(c));
  const splitOf = (cat: string) => {
    const vocab = vocabWordKeys(categories[cat]);
    const raw = Object.keys(categories[cat] ?? {}).filter(k => !k.includes('_'));
    return vocabSplit(languageCode, cat, vocab.length, presentInUnit, { raw, vocab });
  };

  UNIT_CONFIGS.forEach((unit, unitIndex) => {
    const unitNum = unitIndex + 1;
    const cats = unit.categories.filter(c => categoryList.includes(c));

    // Vocabulaire (conversation juste après sa catégorie d'ancrage), avec la
    // grammaire intercalée entre les catégories — même répartition que le sentier
    const gParts = grammarPartsForUnit(categories['Grammaire'], unitNum);
    const slots = grammarSlots(cats.length, gParts.length);
    cats.forEach((cat, k) => {
      const keys = vocabWordKeys(categories[cat]);
      if (keys.length > TRAIL_SPLIT_SIZE || LESSON_BREAKS[cat]) {
        splitOf(cat).head.forEach(range =>
          steps.push({ kind: 'vocab', unitId: unit.id, category: cat, range }));
      } else if (keys.length > 0) {
        steps.push({ kind: 'vocab', unitId: unit.id, category: cat });
      }
      const conv = CONVERSATION_TRAIL_CONFIGS.find(
        c => c.afterCategory === cat && c.unitId === unit.id && c.languageCodes.includes(languageCode)
      );
      if (conv) steps.push({ kind: 'conversation', unitId: unit.id, conversationId: conv.conversationId });
      // Fins de catégories reportées après celle-ci (ex. Verbes · 3 après Temps)
      categoryList.forEach(other => {
        if (vocabWordKeys(categories[other]).length <= TRAIL_SPLIT_SIZE) return;
        const split = splitOf(other);
        if (split.tailAnchor?.unitId === unit.id && split.tailAnchor.afterCategory === cat) {
          split.tail.forEach(range => steps.push({ kind: 'vocab', unitId: unit.id, category: other, range }));
        }
      });
      gParts.forEach((subcats, j) => {
        if (slots[j] === k) steps.push({ kind: 'grammar', unitId: unit.id, subcats });
      });
    });
    gParts.forEach((subcats, j) => {
      if (slots[j] === -1) steps.push({ kind: 'grammar', unitId: unit.id, subcats });
    });

    // Phrases (seulement celles qui ont réellement du contenu)
    const sentCats = UNIT_SENTENCE_CATEGORIES[unitIndex] ?? [];
    // Catégories vides écartées avant le regroupement, comme sur le sentier.
    const counts = sentCats.map(c => [
      c,
      new Set(sentences.filter(s => s.category === c).map(s => s.original)).size,
    ] as [string, number]).filter(([, n]) => n > 0);
    // Kama : une étape par thème, comme sur le sentier.
    (languageCode === 'ka' ? counts.map(([c]) => [c]) : groupParts(counts))
      .forEach(cats => steps.push({ kind: 'phrases', unitId: unit.id, cats }));
  });

  return steps;
};

/** Position de l'étape en cours dans la séquence (-1 si introuvable). */
export const findTrailStepIndex = (
  seq: TrailNavStep[],
  current:
    | { kind: 'vocab'; category: string; range?: { start: number; end: number } | null }
    | { kind: 'conversation'; conversationId: string }
    | { kind: 'grammar'; unitId: number; subcats?: string[] | null }
    | { kind: 'phrases'; cats: string[] }
): number => {
  const lastIndex = (pred: (s: TrailNavStep) => boolean) => {
    for (let i = seq.length - 1; i >= 0; i--) if (pred(seq[i])) return i;
    return -1;
  };
  switch (current.kind) {
    case 'vocab': {
      const r = current.range;
      if (r) {
        const exact = seq.findIndex(s => s.kind === 'vocab' && s.category === current.category && s.range?.start === r.start);
        if (exact !== -1) return exact;
      }
      // Catégorie ouverte en entier → on la considère terminée
      return lastIndex(s => s.kind === 'vocab' && s.category === current.category);
    }
    case 'conversation':
      return seq.findIndex(s => s.kind === 'conversation' && s.conversationId === current.conversationId);
    case 'grammar': {
      const sub = current.subcats;
      if (sub && sub.length) {
        const exact = seq.findIndex(s => s.kind === 'grammar' && s.unitId === current.unitId
          && s.subcats.length === sub.length && s.subcats.every((x, i) => x === sub[i]));
        if (exact !== -1) return exact;
      }
      return lastIndex(s => s.kind === 'grammar' && s.unitId === current.unitId);
    }
    case 'phrases':
      return lastIndex(s => s.kind === 'phrases' && s.cats.some(c => current.cats.includes(c)));
  }
};

/** Prochaine étape après `index` ; `skipPremium` saute les étapes Premium. */
export const nextTrailStep = (seq: TrailNavStep[], index: number, skipPremium = false): TrailNavStep | null => {
  for (let i = index + 1; i < seq.length; i++) {
    if (!skipPremium || !isPremiumStep(seq[i])) return seq[i];
  }
  return null;
};
